import React, { useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import GuardsAlarmsQuotationPreview from "./GuardsAlarmsQuotationPreview";
import BrochurePicker from "../components/BrochurePicker";
import GenericQuotationPreview from "./GenericQuotationPreview";
import { qualifiesForGuardsAlarmsLayout } from "../salesStatusMaps";
import { useUploadQuotationMutation } from "../salesApi";
import { useGetCompanyInfoQuery } from "../../settings/settingsApi";

// Generate-and-send flow for a quotation PDF - same html2canvas+jsPDF
// recipe as StockDocumentPrintModal.jsx's LPO email path (per direct
// instruction to reuse that approach rather than a new PDF technique): the
// visible preview node is captured exactly as rendered, embedded as a
// full-page image into an A4 PDF, then uploaded via the existing
// POST .../quotation endpoint (source-agnostic - it doesn't care whether
// the bytes came from this generator or a manually-attached file).
const QuotationPreviewModal = ({ show, onClose, enquiry, guardRequirements, serviceTypes }) => {
  const previewRef = useRef(null);
  const [isSending, setIsSending] = useState(false);
  const [uploadQuotation] = useUploadQuotationMutation();
  // Cleared when the modal closes so brochures never silently carry over into
  // a different send - they are not stored, so there is nothing to restore.
  const [brochures, setBrochures] = useState([]);
  const { data: company } = useGetCompanyInfoQuery(enquiry?.branchId, { skip: !enquiry });

  if (!enquiry) return null;

  const isRateBased = qualifiesForGuardsAlarmsLayout(enquiry, serviceTypes);

  const handleSend = async () => {
    setIsSending(true);
    try {
      const canvas = await html2canvas(previewRef.current, { scale: 2 });
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;
      pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, imgHeight);
      const pdfBlob = pdf.output("blob");

      await uploadQuotation({ recordGuid: enquiry.recordGuid, file: pdfBlob, brochures }).unwrap();
      toast.success("Quotation generated and sent");
      onClose();
    } catch (error) {
      toast.error(error?.message || "Could not generate/send the quotation");
    } finally {
      setIsSending(false);
    }
  };

  const handlePrint = () => {
    const win = window.open("", "_blank", "width=900,height=1000");
    if (!win || !previewRef.current) return;
    win.document.write(
      `<html><head><title>Quotation - ${enquiry.enquiryNumber}</title>` +
        `<style>@page { size: A4; margin: 10mm; } html,body { margin:0; padding:0; }</style></head>` +
        `<body onload="window.print();window.close();">${previewRef.current.innerHTML}</body></html>`
    );
    win.document.close();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Quotation Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "#e9ecef" }}>
        <div style={{ border: "1px solid #ccc" }} ref={previewRef}>
          {isRateBased ? (
            <GuardsAlarmsQuotationPreview enquiry={enquiry} guardRequirements={guardRequirements} company={company} />
          ) : (
            <GenericQuotationPreview enquiry={enquiry} serviceTypes={serviceTypes} company={company} />
          )}
        </div>
      </Modal.Body>
      {/* Sits outside the captured preview node (previewRef) on purpose - it
          must not end up rendered into the PDF. */}
      <div className="px-3 pt-2">
        <BrochurePicker files={brochures} onChange={setBrochures} disabled={isSending} />
      </div>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="outline-primary" onClick={handlePrint}>Print</Button>
        <Button variant="primary" onClick={handleSend} disabled={isSending}>
          {isSending ? "Sending..." : "Send Quotation"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuotationPreviewModal;
